/**
 * Generate a simple, branded PDF for the document wizards.
 *
 * Uses pdf-lib (no Chromium / no server). Produces a single A4 page
 * with: brand mark + wordmark, document type heading, the form's
 * key/value content, the proof hash + ISO timestamp, a footer line.
 *
 * The output is intentionally minimal — the goal is "looks
 * professional enough to attach to an email," not pixel-perfect
 * publishing.
 */

import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';

export interface PdfSection {
  /** Section heading shown in bold above the body. Optional. */
  heading?: string;
  /** Lines under the heading; each line is rendered on its own row. */
  lines: readonly string[];
}

export interface BuildPdfInput {
  /** Document type label, e.g. "Invoice", "Contract", "Memo". */
  kind: string;
  /** Optional title rendered after the kind, e.g. invoice number. */
  title?: string;
  /** Sections rendered in order, top to bottom. */
  sections: readonly PdfSection[];
  /** SHA-256 hex of the canonical document content. */
  hash: string;
  /** ISO 8601 timestamp of signing. */
  timestamp: string;
  /** Email of the signer. */
  signer: string;
}

const PAGE_W = 595;          // A4 width in points
const PAGE_H = 842;          // A4 height in points
const MARGIN = 56;
const BRAND_COLOR = rgb(26 / 255, 54 / 255, 93 / 255); // #1A365D — matches --c-spark
const INK_COLOR = rgb(11 / 255, 13 / 255, 16 / 255);
const MUTED_COLOR = rgb(82 / 255, 88 / 255, 97 / 255);
const SUBTLE_COLOR = rgb(130 / 255, 137 / 255, 146 / 255);
const RULE_COLOR = rgb(226 / 255, 228 / 255, 232 / 255);

export async function buildDocumentPdf(input: BuildPdfInput): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const page = doc.addPage([PAGE_W, PAGE_H]);
  const helv = await doc.embedFont(StandardFonts.Helvetica);
  const helvBold = await doc.embedFont(StandardFonts.HelveticaBold);
  const courier = await doc.embedFont(StandardFonts.Courier);

  let y = PAGE_H - MARGIN;

  // ── Header ────────────────────────────────────────────────────
  drawAtomMark(page, MARGIN, y - 18, BRAND_COLOR);
  page.drawText('Growing Intelligence', {
    x: MARGIN + 30,
    y: y - 14,
    size: 13,
    font: helvBold,
    color: BRAND_COLOR,
  });
  y -= 36;

  // Horizontal rule under header
  page.drawLine({
    start: { x: MARGIN, y },
    end:   { x: PAGE_W - MARGIN, y },
    thickness: 0.5,
    color: RULE_COLOR,
  });
  y -= 28;

  // ── Title ─────────────────────────────────────────────────────
  page.drawText(input.kind.toUpperCase(), {
    x: MARGIN,
    y,
    size: 22,
    font: helvBold,
    color: INK_COLOR,
  });
  y -= 24;
  if (input.title) {
    page.drawText(input.title, {
      x: MARGIN,
      y,
      size: 12,
      font: helv,
      color: MUTED_COLOR,
    });
    y -= 18;
  }
  y -= 12;

  // ── Sections ──────────────────────────────────────────────────
  const lineHeight = 14;
  const bodySize = 11;
  const headingSize = 11;
  for (const section of input.sections) {
    // Page break if too low on the page.
    if (y < MARGIN + 120) break;
    if (section.heading) {
      page.drawText(section.heading.toUpperCase(), {
        x: MARGIN,
        y,
        size: headingSize - 1,
        font: helvBold,
        color: SUBTLE_COLOR,
      });
      y -= 14;
    }
    for (const raw of section.lines) {
      const wrapped = wrapText(raw, helv, bodySize, PAGE_W - MARGIN * 2);
      for (const ln of wrapped) {
        if (y < MARGIN + 100) break;
        page.drawText(ln, {
          x: MARGIN,
          y,
          size: bodySize,
          font: helv,
          color: INK_COLOR,
        });
        y -= lineHeight;
      }
    }
    y -= 10;
  }

  // ── Proof block ───────────────────────────────────────────────
  const proofY = MARGIN + 60;
  page.drawLine({
    start: { x: MARGIN, y: proofY + 26 },
    end:   { x: PAGE_W - MARGIN, y: proofY + 26 },
    thickness: 0.5,
    color: RULE_COLOR,
  });
  page.drawText('PROOF', {
    x: MARGIN,
    y: proofY + 10,
    size: 9,
    font: helvBold,
    color: SUBTLE_COLOR,
  });
  page.drawText(`Hash:      ${input.hash}`, {
    x: MARGIN,
    y: proofY - 4,
    size: 8,
    font: courier,
    color: INK_COLOR,
  });
  page.drawText(`Timestamp: ${input.timestamp}`, {
    x: MARGIN,
    y: proofY - 16,
    size: 8,
    font: courier,
    color: INK_COLOR,
  });
  page.drawText(`Signer:    ${input.signer}`, {
    x: MARGIN,
    y: proofY - 28,
    size: 8,
    font: courier,
    color: INK_COLOR,
  });

  // ── Footer ────────────────────────────────────────────────────
  const footerText = 'Signed with GI Engine — growing-intelligence.com';
  const footerWidth = helv.widthOfTextAtSize(footerText, 9);
  page.drawText(footerText, {
    x: (PAGE_W - footerWidth) / 2,
    y: MARGIN - 12,
    size: 9,
    font: helv,
    color: SUBTLE_COLOR,
  });

  return doc.save();
}

export function downloadPdf(filename: string, bytes: Uint8Array) {
  // Copy into a fresh ArrayBuffer so the Blob has a stable backing
  // store independent of the Uint8Array's lifetime.
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  const blob = new Blob([copy], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── helpers ────────────────────────────────────────────────────

/** Simple word-wrap. Long single tokens are split mid-word as needed. */
function wrapText(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
  const words = text.replace(/\s+/g, ' ').split(' ');
  const lines: string[] = [];
  let current = '';
  for (const w of words) {
    const candidate = current.length === 0 ? w : `${current} ${w}`;
    const width = font.widthOfTextAtSize(candidate, size);
    if (width > maxWidth && current.length > 0) {
      lines.push(current);
      current = w;
    } else {
      current = candidate;
    }
  }
  if (current.length > 0) lines.push(current);
  return lines;
}

/** Render a tiny GI atom mark — outer ring + tilted equator + core dot.
 *  Letters are omitted at this size so it reads cleanly at 18pt. */
function drawAtomMark(page: PDFPage, cx: number, cy: number, color: ReturnType<typeof rgb>) {
  const r = 9;
  page.drawCircle({ x: cx, y: cy, size: r,     borderColor: color, borderWidth: 0.6, opacity: 0.6 });
  page.drawCircle({ x: cx, y: cy, size: r * 0.36, borderColor: color, borderWidth: 0.7, opacity: 0.6 });
  page.drawCircle({ x: cx, y: cy, size: 1.6,   color });
}
